import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, Send, Loader2, Mail, Sparkles, RotateCcw, Crown, Gift, AlertCircle, CheckCircle2, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type Campaign = {
  id: "welcome" | "upgrade_promo" | "winback_30" | "winback_60" | "free_trial";
  title: string;
  description: string;
  icon: React.ReactNode;
  fn: "send-bulk-campaign" | "send-free-trial-promo";
  payloadKey?: string; // for send-bulk-campaign
  previewPath?: string;
};

const CAMPAIGNS: Campaign[] = [
  {
    id: "welcome",
    title: "Welcome Email",
    description: "Onboarding email introducing OdyVoy's core features.",
    icon: <Sparkles className="h-5 w-5" />,
    fn: "send-bulk-campaign",
    payloadKey: "welcome",
  },
  {
    id: "upgrade_promo",
    title: "Upgrade Promo",
    description: "Pitches Premium Economy and First Class benefits.",
    icon: <Crown className="h-5 w-5" />,
    fn: "send-bulk-campaign",
    payloadKey: "upgrade_promo",
    previewPath: "/emails/upgrade",
  },
  {
    id: "winback_30",
    title: "Win-Back (30-day)",
    description: "10% off code for users inactive for 30+ days.",
    icon: <RotateCcw className="h-5 w-5" />,
    fn: "send-bulk-campaign",
    payloadKey: "winback_30",
    previewPath: "/emails/win-back",
  },
  {
    id: "winback_60",
    title: "Win-Back (60-day)",
    description: "15% off code for users inactive for 60+ days.",
    icon: <RotateCcw className="h-5 w-5" />,
    fn: "send-bulk-campaign",
    payloadKey: "winback_60",
    previewPath: "/emails/win-back",
  },
];

export default function EmailCampaignsAdmin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [testEmails, setTestEmails] = useState<Record<string, string>>({});
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, { sent: number; failed?: number; skipped?: number; test?: boolean }>>({});

  const send = async (c: Campaign, mode: "test" | "all") => {
    const id = `${c.id}-${mode}`;
    setSendingId(id);
    try {
      const testEmail = testEmails[c.id]?.trim();
      if (mode === "test" && !testEmail) {
        toast({ title: "Enter a test email", variant: "destructive" });
        setSendingId(null);
        return;
      }

      if (mode === "all") {
        const ok = window.confirm(
          `Send "${c.title}" to ALL eligible users? This cannot be undone.`
        );
        if (!ok) { setSendingId(null); return; }
      }

      let body: Record<string, unknown> = {};
      if (c.fn === "send-bulk-campaign") {
        body = { campaign: c.payloadKey, ...(mode === "test" ? { testEmail } : {}) };
      } else {
        // send-free-trial-promo: no test mode supported by that function — fall back to bulk
        if (mode === "test") {
          // Use bulk-campaign welcome shell isn't relevant; just inform the user
          toast({
            title: "Test mode not supported",
            description: "Free Trial Promo only supports sending to all users. Use Preview to view the email.",
            variant: "destructive",
          });
          setSendingId(null);
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke(c.fn, { body });
      if (error) throw error;

      const sent = (data as any)?.sent ?? 0;
      const failed = (data as any)?.failed ?? 0;
      const skipped = (data as any)?.skipped ?? 0;
      setResults((r) => ({ ...r, [c.id]: { sent, failed, skipped, test: mode === "test" } }));
      toast({
        title: mode === "test" ? "Test email sent" : "Campaign sent",
        description: mode === "test"
          ? `Sent test to ${testEmail}.`
          : `Sent: ${sent}, Failed: ${failed}, Skipped: ${skipped}`,
      });
    } catch (e: any) {
      console.error(e);
      toast({
        title: "Send failed",
        description: e?.message || "Could not send campaign.",
        variant: "destructive",
      });
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
            <Mail className="h-7 w-7 text-primary" />
            Email Campaigns
          </h1>
          <p className="text-muted-foreground">
            Send welcome, upgrade, win-back and free-trial campaigns from <code className="text-xs bg-muted px-1 py-0.5 rounded">sales.marketing@mail.odyvoy.com</code>.
          </p>
        </div>

        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Before sending to everyone</AlertTitle>
          <AlertDescription>
            Always send a <strong>test email to yourself first</strong>. Bulk sends skip unsubscribed users automatically and respect List-Unsubscribe headers.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          {CAMPAIGNS.map((c) => {
            const r = results[c.id];
            const isSending = sendingId?.startsWith(c.id);
            return (
              <Card key={c.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <span className="text-primary">{c.icon}</span>
                    {c.title}
                  </CardTitle>
                  <CardDescription>{c.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
                    <div>
                      <Label htmlFor={`test-${c.id}`} className="text-xs text-muted-foreground">
                        Test recipient
                      </Label>
                      <Input
                        id={`test-${c.id}`}
                        type="email"
                        placeholder="you@example.com"
                        value={testEmails[c.id] || ""}
                        onChange={(e) => setTestEmails((t) => ({ ...t, [c.id]: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => send(c, "test")}
                      disabled={!!isSending}
                      className="min-h-12"
                    >
                      {sendingId === `${c.id}-test` ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send test
                    </Button>
                    {c.previewPath && (
                      <Button
                        variant="ghost"
                        onClick={() => window.open(c.previewPath, "_blank")}
                        className="min-h-12"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                    )}
                    <Button
                      onClick={() => send(c, "all")}
                      disabled={!!isSending}
                      className="min-h-12"
                    >
                      {sendingId === `${c.id}-all` ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send to all
                    </Button>
                  </div>

                  {r && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      {r.test
                        ? `Test sent (${r.sent}).`
                        : `Sent: ${r.sent} · Failed: ${r.failed ?? 0} · Skipped: ${r.skipped ?? 0}`}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
